// src/pages/admin/users/AdminUsersPage.jsx
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  auth,
  listenConversations,
  listenMessages,
  sendMessage,
  deleteConversation,
} from "../../../utils/firebase";
import {
  fetchUsers,
  fetchCurrentOperator,
  enableUser,
  disableUser,
  updateUser,
  deleteUser,
  getUser,
  setUserPlan,
  setUserRole,
  createUser,
} from "./adminUsersApi";

// ===== helpers =====
const DAY_MS = 24 * 60 * 60 * 1000;
const PAGE_SIZE = 50;
const SUPPORT_COMPOSER_TEXTAREA_MIN = 36;
const SUPPORT_COMPOSER_TEXTAREA_MAX = 120;
const CANONICAL_PLANS = ["basic", "pro", "premium"];

async function fetchOperatorRoleFromToken(user) {
  if (!user) return "user";
  try {
    const r = await user.getIdTokenResult(true);
    const c = r.claims || {};
    const cr = String(c.role || "").trim().toLowerCase();
    if (cr === "super.admin") return "super.admin";
    if (c.admin === true || cr === "admin") return "admin";
    return "user";
  } catch {
    return "user";
  }
}

/** Primary: backend GET /api/admin/users/me (normalizeDecodedRole + SUPER_ADMIN_EMAILS). Fallback: ID token claims. */
async function resolveOperatorRole(user) {
  if (!user) return "user";
  try {
    const data = await fetchCurrentOperator();
    if (data && data.isSuperAdmin === true) return "super.admin";
    const raw = data?.normalizedRole ?? data?.role;
    const r = String(raw == null ? "" : raw)
      .trim()
      .toLowerCase();
    if (r === "super.admin" || r === "admin" || r === "user") return r;
    if (data && data.isAdmin === true) return "admin";
  } catch {
    /* fallback below */
  }
  return fetchOperatorRoleFromToken(user);
}

function resolveRowRole(user) {
  const r = user?.role;
  if (r === "user" || r === "admin" || r === "super.admin") return r;
  const c = user?.customClaims || {};
  const cr = String(c.role || "").trim().toLowerCase();
  if (cr === "super.admin") return "super.admin";
  if (c.admin === true || cr === "admin") return "admin";
  return "user";
}

function roleLabel(role) {
  if (role === "super.admin") return "Super Admin";
  if (role === "admin") return "Admin";
  return "User";
}

function rowMatchesSelectedCreator(creatorFilter, u) {
  if (!creatorFilter || creatorFilter === "all") return true;
  const want = String(creatorFilter).trim().toLowerCase();
  const a = String(u?.createdByAdminEmail ?? "").trim().toLowerCase();
  if (a) return a === want;
  const b = String(u?.createdByEmail ?? "").trim().toLowerCase();
  if (b) return b === want;
  return false;
}

/** Email text color by account role (dark UI): super = gold, admin = Pro blue, user = default white. */
function emailTextColorForRole(role) {
  if (role === "super.admin") return "#fcd34d";
  if (role === "admin") return "#93c5fd";
  return "#fff";
}

function canManageUserRow(operatorRole, myUid, u) {
  if (!u?.uid) return false;
  if (operatorRole === "super.admin") return true;
  if (operatorRole !== "admin") return false;
  const uid = String(u.uid);
  const self = myUid && String(myUid) === uid;
  if (self) return true;
  if (resolveRowRole(u) !== "user") return false;
  return String(u.createdByAdminUid || u.createdByUid || "") === String(myUid || "");
}

function canDeleteUserRow(operatorRole, myUid, u) {
  if (operatorRole !== "super.admin") return false;
  if (!u?.uid || !myUid) return false;
  if (String(u.uid) === String(myUid)) return false;
  return true;
}

function canonicalPlanFromValue(v) {
  if (v == null || v === "") return null;
  const s = String(v).trim().toLowerCase();
  if (CANONICAL_PLANS.includes(s)) return s;
  return null;
}

function canonicalPlanFromAnyResponse(res) {
  if (!res || typeof res !== "object") return null;
  const r = res.plan ?? res.user?.plan ?? res.customClaims?.plan ?? res.data?.plan;
  return canonicalPlanFromValue(r);
}

function resolveUserPlan(user) {
  const raw = user?.plan ?? user?.customClaims?.plan;
  if (raw == null || raw === "") return "basic";
  const s = String(raw).trim().toLowerCase();
  if (s === "basic" || s === "pro" || s === "premium") return s;
  return "basic";
}

/** Priority: planOverrides[uid] → user.plan → customClaims.plan → basic */
function resolveDisplayedPlan(user, overrides) {
  const uid = user?.uid;
  if (uid && overrides && typeof overrides === "object") {
    const o = overrides[uid];
    if (o === "basic" || o === "pro" || o === "premium") return o;
  }
  return resolveUserPlan(user);
}

function isValidEmailLike(s) {
  const v = String(s || "").trim();
  return v.includes("@") && v.length >= 3;
}

function formatMsgTime(createdAt) {
  if (createdAt == null) return "";
  try {
    const ms =
      typeof createdAt?.toMillis === "function"
        ? createdAt.toMillis()
        : typeof createdAt?.seconds === "number"
          ? createdAt.seconds * 1000
          : Date.parse(String(createdAt));
    if (!Number.isFinite(ms)) return "";
    return new Date(ms).toLocaleString();
  } catch {
    return "";
  }
}

function parseEpochMaybe(v) {
  if (v == null) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const t = Date.parse(String(v));
  return Number.isFinite(t) ? t : null;
}

/** Firestore Timestamp | plain object → ms for chat meta */
function convUpdatedAtMs(c) {
  if (!c) return 0;
  const t = c.updatedAt;
  try {
    if (t != null && typeof t.toMillis === "function") return t.toMillis();
    if (t != null && typeof t.seconds === "number") return t.seconds * 1000;
  } catch {
    /* noop */
  }
  return 0;
}

function planSortRank(plan) {
  const p = String(plan || "").toLowerCase();
  if (p === "premium") return 0;
  if (p === "pro") return 1;
  if (p === "basic") return 2;
  return 3;
}

function findUserForConversation(conv, users) {
  if (!conv || !Array.isArray(users)) return null;
  const cid = conv.id != null ? String(conv.id) : "";
  if (cid) {
    const byId = users.find((u) => u?.uid && String(u.uid) === cid);
    if (byId) return byId;
  }
  const uidField = conv.userId != null ? String(conv.userId) : "";
  if (uidField) {
    const byUid = users.find((u) => u?.uid && String(u.uid) === uidField);
    if (byUid) return byUid;
  }
  return null;
}

/** Tokens that look like Firebase uids must not be used as visible support titles */
function isSupportOpaqueIdentityToken(s) {
  const t = String(s ?? "").trim();
  if (t.length < 18) return false;
  if (t.includes("@") || /\s/.test(t)) return false;
  return /^[a-zA-Z0-9_-]+$/.test(t);
}

function supportConversationUsernameIsMeaningful(username, matchedUid, convDocId) {
  const u = username != null ? String(username).trim() : "";
  if (!u) return false;
  if (isValidEmailLike(u)) return true;
  const mu = matchedUid != null ? String(matchedUid) : "";
  if (mu && u === mu) return false;
  const cid = convDocId != null ? String(convDocId) : "";
  if (cid && u === cid) return false;
  if (isSupportOpaqueIdentityToken(u)) return false;
  return u.length >= 2;
}

/** Admin user list includes providerData from Firebase Admin (anonymous = single anonymous provider) */
function userRecordLooksFirebaseAnonymous(user) {
  const providers = user?.providerData;
  if (!Array.isArray(providers) || providers.length !== 1) return false;
  return String(providers[0]?.providerId || "").toLowerCase() === "anonymous";
}

/**
 * Guest / Firebase-anonymous: no usable email, displayName, or meaningful conv.username —
 * matched may still exist (e.g. providerData-only `anonymous` in users list); never use raw uid as title.
 */
function isSupportConversationAnonymous(matched, conv) {
  if (!matched) return true;

  const displayEmail = matched?.email != null ? String(matched.email).trim() : "";
  if (isValidEmailLike(displayEmail)) return false;

  const displayName =
    matched?.displayName != null && String(matched.displayName).trim()
      ? String(matched.displayName).trim()
      : "";
  if (displayName) return false;

  const cid = conv?.id != null ? String(conv.id) : "";
  if (supportConversationUsernameIsMeaningful(conv?.username, matched?.uid, cid)) return false;

  if (userRecordLooksFirebaseAnonymous(matched)) return true;

  /* Other accounts whose only handle would be uid (no profile fields loaded) */
  return true;
}

function supportConversationIsWaiting(c, readAtById) {
  if (!c) return false;
  const from = String(c.lastFrom ?? "").toLowerCase();
  if (from !== "user") return false;
  const id = c.id != null ? String(c.id) : "";
  if (!id) return false;
  const updated = convUpdatedAtMs(c);
  const seen = readAtById[id];
  if (seen == null || !Number.isFinite(seen)) return true;
  return updated > seen;
}

function msLeft(expiresAt, nowMs) {
  if (!expiresAt || !Number.isFinite(expiresAt)) return null;
  return expiresAt - nowMs;
}

function formatDaysHours(expiresAt, nowMs) {
  const left = msLeft(expiresAt, nowMs);
  if (left == null) return "-";
  if (left <= 0) return "0.00h";

  const d = Math.floor(left / DAY_MS);
  const h = Math.floor((left % DAY_MS) / (60 * 60 * 1000));
  const hh = String(h).padStart(2, "0");
  return `${d}.${hh}h`;
}

// ===== UI: dropdown "trượt chọn" (styled select) =====
function SliderSelect({ value, onChange, options, disabled, style, variant = "neutral", selectStyle, darkOptions }) {
  const variantStyle =
    variant === "status"
      ? getStatusSelectStyle(value)
      : variant === "plan"
        ? getPlanSelectStyle(value)
        : variant === "role"
          ? getRoleSelectStyle(value)
          : {};

  const optionBaseStyle = darkOptions
    ? { background: "#0f172a", color: "#f8fafc" }
    : { color: "#0f172a" };

  return (
    <div style={{ position: "relative", display: "inline-flex", width: "100%", ...style }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{
          width: "100%",
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          padding: "6px 20px 6px 8px",
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.18)",
          background: "rgba(0,0,0,0.25)",
          color: "#fff",
          fontWeight: 500,
          fontSize: 11,
          outline: "none",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "all 140ms ease",
          ...variantStyle,
          ...(selectStyle || {}),
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ ...optionBaseStyle, ...(o.optionStyle || {}) }}>
            {o.label}
          </option>
        ))}
      </select>

      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          right: 7,
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          opacity: disabled ? 0.5 : 0.9,
          fontSize: 9,
        }}
      >
        ▼
      </div>
    </div>
  );
}

function getStatusSelectStyle(value) {
  if (value === "open") {
    return {
      color: "#6ee7b7",
      borderColor: "rgba(52,211,153,0.38)",
      background: "linear-gradient(180deg, rgba(16,185,129,0.14) 0%, rgba(0,0,0,0.22) 100%)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
    };
  }
  if (value === "lock") {
    return {
      color: "#fca5a5",
      borderColor: "rgba(248,113,113,0.4)",
      background: "linear-gradient(180deg, rgba(220,38,38,0.14) 0%, rgba(0,0,0,0.22) 100%)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
    };
  }
  return { color: "#fff" };
}

function getPlanSelectStyle(value) {
  const v = String(value || "basic").toLowerCase();
  if (v === "pro") {
    return {
      color: "#93c5fd",
      borderColor: "rgba(96,165,250,0.42)",
      background: "linear-gradient(180deg, rgba(37,99,235,0.18) 0%, rgba(0,0,0,0.22) 100%)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
    };
  }
  if (v === "premium") {
    return {
      color: "#fcd34d",
      borderColor: "rgba(251,191,36,0.42)",
      background: "linear-gradient(180deg, rgba(217,119,6,0.16) 0%, rgba(0,0,0,0.22) 100%)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
    };
  }
  return {
    color: "#cbd5e1",
    borderColor: "rgba(100,116,139,0.38)",
    background: "linear-gradient(180deg, rgba(71,85,105,0.2) 0%, rgba(0,0,0,0.24) 100%)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
  };
}

function getRoleSelectStyle(value) {
  const v = String(value || "user").toLowerCase();
  if (v === "super.admin") {
    return {
      color: "#fcd34d",
      borderColor: "rgba(251,191,36,0.42)",
      background: "linear-gradient(180deg, rgba(217,119,6,0.16) 0%, rgba(0,0,0,0.22) 100%)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
    };
  }
  if (v === "admin") {
    return {
      color: "#93c5fd",
      borderColor: "rgba(96,165,250,0.42)",
      background: "linear-gradient(180deg, rgba(37,99,235,0.18) 0%, rgba(0,0,0,0.22) 100%)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
    };
  }
  return {
    color: "#cbd5e1",
    borderColor: "rgba(100,116,139,0.38)",
    background: "linear-gradient(180deg, rgba(71,85,105,0.2) 0%, rgba(0,0,0,0.24) 100%)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
  };
}

// ===== UI: confirm modal =====
function ConfirmModal({ open, title, desc, onCancel, onConfirm, busy }) {
  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          width: 420,
          maxWidth: "100%",
          background: "rgba(10,10,10,0.95)",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.18)",
          borderRadius: 16,
          padding: 16,
          boxShadow: "0 18px 60px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 13, opacity: 0.9, lineHeight: 1.4, whiteSpace: "pre-wrap", fontWeight: 400 }}>
          {desc}
        </div>

        <div style={{ marginTop: 14, display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "transparent",
              color: "#fff",
              fontWeight: 500,
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            Huỷ
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            style={{
              padding: "10px 12px",
              border: 0,
              background: busy ? "#7a2323" : "#ff3b30",
              color: "#fff",
              fontWeight: 700,
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            {busy ? "Đang xoá..." : "Đồng ý"}
          </button>
        </div>
      </div>
    </div>
  );
}

const EMPTY_CONFIRM = { open: false, uid: "", email: "" };

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | stop
  const [creatorFilter, setCreatorFilter] = useState("all");
  const [adminCreators, setAdminCreators] = useState([]);

  // server paging
  const [page, setPage] = useState(1); // 1-based UI page index
  const [nextPageToken, setNextPageToken] = useState(null);

  // tokenByPage[1] = null, tokenByPage[2] = token để fetch page 2, ...
  const tokenByPageRef = useRef({ 1: null });

  const [busyUid, setBusyUid] = useState("");

  const [confirm, setConfirm] = useState(EMPTY_CONFIRM);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const autoDisabledRef = useRef(new Set());
  const lastAutoRunRef = useRef(0);

  const [editUid, setEditUid] = useState("");
  const [editSign, setEditSign] = useState("+");
  const [editNum, setEditNum] = useState("");
  /** Super Admin only: uid whose role/permission row is expanded (hidden for other operators). */
  const [permissionPanelUid, setPermissionPanelUid] = useState("");

  /** uid → canonical plan; shields UI from stale list fetches after PATCH */
  const [planOverrides, setPlanOverrides] = useState({});
  const planRefreshTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (planRefreshTimeoutRef.current) {
        clearTimeout(planRefreshTimeoutRef.current);
        planRefreshTimeoutRef.current = null;
      }
    };
  }, []);

  // email admin đang đăng nhập (hiển thị góc trái + chặn thao tác nguy hiểm lên chính mình)
  const adminEmail = auth?.currentUser?.email || "";

  const [operatorRole, setOperatorRole] = useState("user");
  const [myUid, setMyUid] = useState("");

  useEffect(() => {
    if (!auth) {
      setOperatorRole("user");
      setMyUid("");
      return undefined;
    }
    const unsub = onAuthStateChanged(auth, async (user) => {
      setMyUid(user?.uid ? String(user.uid) : "");
      if (!user) {
        setOperatorRole("user");
        return;
      }
      const role = await resolveOperatorRole(user);
      setOperatorRole(role);
    });
    return () => {
      try {
        unsub();
      } catch {
        /* noop */
      }
    };
  }, []);

  const isCurrentUserSuperAdmin = operatorRole === "super.admin";

  useEffect(() => {
    setPermissionPanelUid("");
  }, [page, q, statusFilter, creatorFilter]);

  const [openPanel, setOpenPanel] = useState(null); // null | "create" | "support"

  const [convList, setConvList] = useState([]);
  const [selConv, setSelConv] = useState("");
  const [convMsgs, setConvMsgs] = useState([]);
  const [chatDraft, setChatDraft] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [chatErr, setChatErr] = useState("");
  const chatTextareaRef = useRef(null);
  const supportMsgsEndRef = useRef(null);

  /** conversationId → last seen updatedAt ms (client-only "read") */
  const [supportReadAt, setSupportReadAt] = useState({});
  const [supportDeleteConfirm, setSupportDeleteConfirm] = useState({ open: false, id: "" });
  const [supportDeleteBusy, setSupportDeleteBusy] = useState(false);
  /** Chỉ bật listener hỗ trợ sau khi Firebase Auth đã báo trạng thái (uid hoặc đã chắc chắn null). */
  const [supportFirebaseUid, setSupportFirebaseUid] = useState(undefined);
  const [supportLoadError, setSupportLoadError] = useState("");

  const supportQueue = useMemo(() => {
    const raw = Array.isArray(convList) ? convList : [];
    const enriched = raw
      .map((c) => {
        const cid = c?.id != null ? String(c.id) : "";
        if (!cid) return null;
        const matched = findUserForConversation(c, users);
        const isAnonymous = isSupportConversationAnonymous(matched, c);

        let resolvedPlan = null;
        let planLabel = "Anonymous";
        let title = "";

        if (!isAnonymous) {
          const plan = resolveDisplayedPlan(matched, planOverrides);
          resolvedPlan = plan === "premium" || plan === "pro" || plan === "basic" ? plan : null;
          planLabel =
            resolvedPlan === "premium"
              ? "Premium"
              : resolvedPlan === "pro"
                ? "Pro"
                : resolvedPlan === "basic"
                  ? "Basic"
                  : "Anonymous";
          const displayEmail = matched?.email != null ? String(matched.email).trim() : "";
          const displayName =
            matched?.displayName != null && String(matched.displayName).trim()
              ? String(matched.displayName).trim()
              : "";
          const uname = c?.username != null ? String(c.username).trim() : "";
          title =
            displayName ||
            (isValidEmailLike(displayEmail) ? displayEmail : "") ||
            (supportConversationUsernameIsMeaningful(c?.username, matched?.uid, cid) ? uname : "");
        }

        return {
          id: cid,
          raw: c,
          matched,
          resolvedPlan,
          planLabel,
          title,
          isAnonymous,
          updatedMs: convUpdatedAtMs(c),
          preview: String(c?.lastText ?? "").slice(0, 72),
          waiting: supportConversationIsWaiting(c, supportReadAt),
        };
      })
      .filter(Boolean);

    enriched.sort((a, b) => {
      const ra = planSortRank(a.resolvedPlan);
      const rb = planSortRank(b.resolvedPlan);
      if (ra !== rb) return ra - rb;
      return (b.updatedMs || 0) - (a.updatedMs || 0);
    });

    let anonOrd = 0;
    for (const row of enriched) {
      if (row.isAnonymous) {
        row.title = anonOrd === 0 ? "Vô Danh" : `Vô Danh ${anonOrd}`;
        anonOrd += 1;
      }
    }

    return enriched;
  }, [convList, users, planOverrides, supportReadAt]);

  const supportWaitingCount = useMemo(() => {
    let n = 0;
    for (const row of supportQueue) {
      if (row.waiting) n += 1;
    }
    return n;
  }, [supportQueue]);

  const selectedSupportRow = useMemo(
    () => supportQueue.find((r) => r.id === selConv) || null,
    [supportQueue, selConv]
  );

  const [supportLayoutNarrow, setSupportLayoutNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 640px)");
    const apply = () => setSupportLayoutNarrow(!!mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const markSupportConversationRead = (cid) => {
    const id = cid != null ? String(cid) : "";
    if (!id) return;
    const c = (Array.isArray(convList) ? convList : []).find((x) => x?.id != null && String(x.id) === id);
    const ms = c ? convUpdatedAtMs(c) : Date.now();
    setSupportReadAt((prev) => ({ ...prev, [id]: ms }));
  };

  useEffect(() => {
    if (!auth) {
      setSupportFirebaseUid(null);
      setSupportLoadError("Firebase chưa sẵn sàng.");
      setConvList([]);
      setConvMsgs([]);
      return undefined;
    }
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      const uid = user?.uid ? String(user.uid) : null;
      setSupportFirebaseUid(uid);
      if (!uid) {
        setConvList([]);
        setConvMsgs([]);
        setSupportLoadError("Chưa có phiên đăng nhập Firebase. Không thể tải hội thoại hỗ trợ.");
      } else {
        setSupportLoadError("");
      }
    });
    return () => {
      try {
        unsubAuth();
      } catch {
        /* noop */
      }
    };
  }, []);

  useEffect(() => {
    if (!supportFirebaseUid) {
      return undefined;
    }
    const unsub = listenConversations(
      (items) => {
        setConvList(Array.isArray(items) ? items : []);
        setSupportLoadError("");
      },
      (err) => {
        setSupportLoadError(
          String(err?.message || err?.code || "Không tải được danh sách hội thoại (Firestore).")
        );
      }
    );
    return () => {
      try {
        unsub();
      } catch {
        /* noop */
      }
    };
  }, [supportFirebaseUid]);

  useEffect(() => {
    if (!supportFirebaseUid) {
      setConvMsgs([]);
      return undefined;
    }
    if (!selConv) {
      setConvMsgs([]);
      return undefined;
    }
    const unsub = listenMessages(selConv, (items) => {
      setConvMsgs(Array.isArray(items) ? items : []);
    });
    return () => {
      try {
        unsub();
      } catch {
        /* noop */
      }
    };
  }, [supportFirebaseUid, selConv]);

  const [cuEmail, setCuEmail] = useState("");
  const [cuPassword, setCuPassword] = useState("");
  const [cuRole, setCuRole] = useState("user");
  const [cuPlan, setCuPlan] = useState("basic");
  const [cuDays, setCuDays] = useState("");
  const [cuBusy, setCuBusy] = useState(false);
  const [cuOk, setCuOk] = useState("");
  const [cuErr, setCuErr] = useState("");

  useEffect(() => {
    if (openPanel === "create" && operatorRole !== "super.admin") {
      setCuRole("user");
    }
  }, [openPanel, operatorRole]);

  const toggleCreatePanel = () => setOpenPanel((p) => (p === "create" ? null : "create"));
  const toggleSupportPanel = () => setOpenPanel((p) => (p === "support" ? null : "support"));

  // ---- load page helper: luôn load theo targetPage để tránh bug setState async ----
  const loadPage = async (targetPage) => {
    const p = Math.max(1, Number(targetPage || 1));
    setError("");
    setLoading(true);
    try {
      const pageToken = tokenByPageRef.current[p] ?? null;

      const data = await fetchUsers({
        limit: PAGE_SIZE,
        pageToken: pageToken || undefined,
        q: q || undefined,
        status: statusFilter || "all",
        createdBy: creatorFilter && creatorFilter !== "all" ? creatorFilter : undefined,
      });

      const list = Array.isArray(data.users) ? data.users : [];
      if (Array.isArray(data.adminCreators)) {
        setAdminCreators(data.adminCreators);
      } else {
        setAdminCreators([]);
      }

      setPlanOverrides((prev) => {
        const next = { ...prev };
        for (const row of list) {
          const uid = row?.uid;
          if (!uid || !next[uid]) continue;
          const fetchedCanonical = resolveUserPlan(row);
          if (fetchedCanonical === next[uid]) {
            delete next[uid];
          }
        }
        return next;
      });

      setUsers(list);

      const nxt = data.nextPageToken ? String(data.nextPageToken) : null;
      setNextPageToken(nxt);

      // lưu token trang kế tiếp
      if (nxt) {
        tokenByPageRef.current = { ...tokenByPageRef.current, [p + 1]: nxt };
      }

      setPage(p);
    } catch (e) {
      setError(e?.message || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  // initial
  useEffect(() => {
    loadPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // debounce search/filter để tránh spam
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      // reset tokens về trang 1 mỗi khi đổi q/status/creator/adminEmail
      tokenByPageRef.current = { 1: null };
      setNextPageToken(null);
      loadPage(1);
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, statusFilter, creatorFilter, adminEmail]);

  const normalized = useMemo(() => {
    return (users || []).map((u) => {
      const email = u.email || "";
      const uid = u.uid;

      const expiresAt =
        parseEpochMaybe(u.access?.expiresAt) ??
        parseEpochMaybe(u.expiresAt) ??
        parseEpochMaybe(u.customClaims?.access?.expiresAt) ??
        null;

      const plan = resolveDisplayedPlan(u, planOverrides);

      return { ...u, uid, email, expiresAt, plan };
    });
  }, [users, planOverrides]);

  // auto lock best effort (chỉ áp cho users của trang hiện tại)
  useEffect(() => {
    if (nowMs - lastAutoRunRef.current < 30_000) return;
    lastAutoRunRef.current = nowMs;

    normalized.forEach((u) => {
      if (!u?.uid) return;
      if (!u.expiresAt || !Number.isFinite(u.expiresAt)) return;

      const left = u.expiresAt - nowMs;
      if (left <= 0 && !u.disabled) {
        if (autoDisabledRef.current.has(u.uid)) return;
        autoDisabledRef.current.add(u.uid);

        disableUser(u.uid)
          .then(() => loadPage(page))
          .catch(() => {})
          .finally(() => {
            setTimeout(() => autoDisabledRef.current.delete(u.uid), 120_000);
          });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowMs, normalized]);

  // sort theo yêu cầu TRONG TRANG HIỆN TẠI
  const pageUsers = useMemo(() => {
    const arr = normalized;

    const withSortKey = arr.map((u) => {
      const hasLimit = !!(u.expiresAt && Number.isFinite(u.expiresAt));
      const left = hasLimit ? u.expiresAt - nowMs : null;
      return { u, hasLimit, left };
    });

    withSortKey.sort((a, b) => {
      if (!a.hasLimit && b.hasLimit) return -1;
      if (a.hasLimit && !b.hasLimit) return 1;

      if (!a.hasLimit && !b.hasLimit) {
        const ea = String(a.u.email || "").toLowerCase();
        const eb = String(b.u.email || "").toLowerCase();
        return ea.localeCompare(eb);
      }

      const la = Number(a.left);
      const lb = Number(b.left);
      if (la !== lb) return la - lb;

      const ea = String(a.u.email || "").toLowerCase();
      const eb = String(b.u.email || "").toLowerCase();
      return ea.localeCompare(eb);
    });

    return withSortKey.map((x) => x.u);
  }, [normalized, nowMs]);

  const displayUsers = useMemo(() => {
    if (creatorFilter === "all" || !creatorFilter) return pageUsers;
    return pageUsers.filter((u) => rowMatchesSelectedCreator(creatorFilter, u));
  }, [pageUsers, creatorFilter]);

  const effectiveAdminCreators = useMemo(() => {
    const backend = Array.isArray(adminCreators) ? adminCreators : [];
    const fromBackend = backend.filter((c) => isValidEmailLike(c?.email));

    let base;
    if (fromBackend.length > 0) {
      base = fromBackend;
    } else {
      const byEmail = new Map();

      const mergeRole = (a, b) => {
        const rank = (x) => (x === "super.admin" ? 2 : x === "admin" ? 1 : 0);
        return rank(b) > rank(a) ? b : a;
      };

      const add = (emailRaw, role) => {
        const email = String(emailRaw ?? "").trim();
        if (!isValidEmailLike(email)) return;
        const r = role === "super.admin" || role === "admin" ? role : null;
        if (!r) return;
        const k = email.toLowerCase();
        const prev = byEmail.get(k);
        if (!prev) {
          byEmail.set(k, { email, role: r });
          return;
        }
        byEmail.set(k, { email: prev.email, role: mergeRole(prev.role, r) });
      };

      if ((operatorRole === "admin" || operatorRole === "super.admin") && isValidEmailLike(adminEmail)) {
        add(adminEmail, operatorRole);
      }

      for (const u of pageUsers) {
        const rr = resolveRowRole(u);
        if (rr === "admin" || rr === "super.admin") add(u.email, rr);
      }

      base = Array.from(byEmail.values());
    }

    if (creatorFilter && creatorFilter !== "all" && isValidEmailLike(creatorFilter)) {
      const want = String(creatorFilter).trim().toLowerCase();
      const has = base.some((x) => String(x?.email || "").trim().toLowerCase() === want);
      if (!has) {
        let synRole = null;
        if (want === String(adminEmail || "").trim().toLowerCase()) {
          if (operatorRole === "super.admin" || operatorRole === "admin") synRole = operatorRole;
        }
        return [...base, { email: String(creatorFilter).trim(), role: synRole }];
      }
    }

    return base;
  }, [adminCreators, operatorRole, adminEmail, pageUsers, creatorFilter]);

  const onLogout = async () => {
    try {
      await auth.signOut();
    } catch (e) {
      setError(e?.message || "Logout failed");
    }
  };

  const onRoleChange = async (u, newRole) => {
    if (!u?.uid) return;
    if (operatorRole !== "super.admin") return;
    if (!canManageUserRow(operatorRole, myUid, u)) return;
    const cur = resolveRowRole(u);
    if (cur === newRole) return;
    setBusyUid(u.uid);
    setError("");
    try {
      await setUserRole(u.uid, newRole);
      if (String(u.uid) === String(myUid) && auth?.currentUser) await auth.currentUser.getIdToken(true);
      if (auth?.currentUser) {
        const nextOp = await resolveOperatorRole(auth.currentUser);
        setOperatorRole(nextOp);
      }
      await loadPage(page);
    } catch (e) {
      setError(e?.message || "Role update failed");
    } finally {
      setBusyUid("");
    }
  };

  const onActionChange = async (u, action) => {
    if (!u?.uid) return;
    if (!canManageUserRow(operatorRole, myUid, u)) {
      setError("Không có quyền thao tác với tài khoản này.");
      return;
    }

    const adminLower = String(adminEmail || "").trim().toLowerCase();
    const uLower = String(u?.email || "").trim().toLowerCase();
    const isSelf = !!(adminLower && uLower && adminLower === uLower);
    const isPlanAction = action === "basic" || action === "pro" || action === "premium";
    if (isSelf && !isPlanAction) {
      setError("Không thể thao tác với tài khoản admin đang đăng nhập.");
      return;
    }

    if (action === "delete") {
      if (!canDeleteUserRow(operatorRole, myUid, u)) return;
      setConfirm({ open: true, uid: u.uid, email: u.email || "-" });
      return;
    }

    if (isPlanAction) {
      try {
        const requestedPlan = canonicalPlanFromValue(action);
        if (!requestedPlan) throw new Error("Invalid plan");

        // If same plan is selected, do nothing (keeps dropdown usable).
        const currentDisplayedPlan = canonicalPlanFromValue(u?.plan);
        if (currentDisplayedPlan === requestedPlan) return;

        setBusyUid(u.uid);
        setError("");

        // PATCH plan using dedicated helper; backend will return canonical plan in top-level `plan`.
        const res = await setUserPlan(u.uid, requestedPlan);

        const canonicalReturned =
          canonicalPlanFromValue(res?.plan) || canonicalPlanFromValue(res?.user?.plan) || null;

        if (!canonicalReturned) {
          throw new Error("Backend did not return a canonical plan");
        }

        // Update row locally only after backend confirmed canonical persisted plan.
        setUsers((prev) =>
          (prev || []).map((row) =>
            row.uid === u.uid
              ? {
                  ...row,
                  plan: canonicalReturned,
                  customClaims: { ...(row.customClaims || {}), plan: canonicalReturned },
                }
              : row
          )
        );

        // Temporary in-memory shield for this session while we do a delayed reload.
        setPlanOverrides((prev) => ({ ...prev, [u.uid]: canonicalReturned }));

        // If self, force refresh token/claims so updated plan becomes effective immediately.
        if (isSelf && auth?.currentUser) await auth.currentUser.getIdToken(true);

        // Safe delayed reload so backend truth becomes visible without clobbering the row immediately.
        if (planRefreshTimeoutRef.current) {
          clearTimeout(planRefreshTimeoutRef.current);
          planRefreshTimeoutRef.current = null;
        }
        const targetPage = page;
        planRefreshTimeoutRef.current = setTimeout(() => {
          loadPage(targetPage).catch(() => {});
        }, 500);
      } catch (e) {
        setError(e?.message || "Plan update failed");

        // Restore last real confirmed plan for this row (avoid leaving a fake plan in UI).
        try {
          const res = await getUser(u.uid);
          const persistedPlan = canonicalPlanFromAnyResponse(res);

          if (persistedPlan) {
            setUsers((prev) =>
              (prev || []).map((row) =>
                row.uid === u.uid
                  ? {
                      ...row,
                      plan: persistedPlan,
                      customClaims: { ...(row.customClaims || {}), plan: persistedPlan },
                    }
                  : row
              )
            );
            setPlanOverrides((prev) => ({ ...prev, [u.uid]: persistedPlan }));
          } else {
            setPlanOverrides((prev) => {
              const next = { ...prev };
              delete next[u.uid];
              return next;
            });
          }
        } catch {
          setPlanOverrides((prev) => {
            const next = { ...prev };
            delete next[u.uid];
            return next;
          });
        }
      } finally {
        setBusyUid("");
      }
      return;
    }

    setBusyUid(u.uid);
    setError("");
    try {
      if (action === "open") await enableUser(u.uid);
      if (action === "lock") await disableUser(u.uid);
      await loadPage(page);
    } catch (e) {
      setError(e?.message || "Action failed");
    } finally {
      setBusyUid("");
    }
  };

  const openEditor = (u) => {
    if (!u?.uid) return;
    if (!canManageUserRow(operatorRole, myUid, u)) return;
    setEditUid((prev) => (prev === u.uid ? "" : u.uid));
    setEditSign("+");
    setEditNum("");
  };

  const cancelEdit = () => {
    setEditUid("");
    setEditSign("+");
    setEditNum("");
  };

  const saveDays = async (u) => {
    if (!canManageUserRow(operatorRole, myUid, u)) return;
    const digits = String(editNum || "").replace(/[^\d]/g, "");
    const n = Number(digits);
    if (!Number.isFinite(n) || n <= 0) {
      setError("Nhập số ngày > 0");
      return;
    }

    const left = msLeft(u.expiresAt, nowMs);
    const baseMs = left == null ? 0 : Math.max(0, left);

    const deltaMs = n * DAY_MS * (editSign === "-" ? -1 : 1);
    const nextMs = Math.max(0, baseMs + deltaMs);
    const nextExpiresAt = nowMs + nextMs;

    setBusyUid(u.uid);
    setError("");
    try {
      await updateUser(u.uid, {
        access: { mode: "days", expiresAt: nextExpiresAt },
        analysisAccess: { blocked: false },
      });

      if (nextMs > 0) await enableUser(u.uid);

      await loadPage(page);
      cancelEdit();
    } catch (e) {
      setError(e?.message || "Set days failed");
    } finally {
      setBusyUid("");
    }
  };

  const confirmDeleteFn = async () => {
    if (!confirm.uid) return;
    setDeleteBusy(true);
    setError("");
    try {
      await deleteUser(confirm.uid);
      setConfirm(EMPTY_CONFIRM);
      await loadPage(page);
    } catch (e) {
      setError(e?.message || "Delete failed");
    } finally {
      setDeleteBusy(false);
    }
  };

  const safePage = Math.max(1, page);
  const canPrev = safePage > 1 && !loading;
  const canNext = !!nextPageToken && !loading;

  const goPrev = () => {
    if (!canPrev) return;
    loadPage(safePage - 1);
  };

  const goNext = () => {
    if (!canNext) return;
    // token trang next đã được lưu vào tokenByPageRef.current[safePage+1]
    loadPage(safePage + 1);
  };

  // Vì server không trả totalCount, ta hiển thị dạng "x/…"
  const pageLabel = `${safePage}/${canNext ? "…" : safePage}`;

  const creatorFilterSelectColor = useMemo(() => {
    if (creatorFilter === "all" || !creatorFilter) return "#fff";
    const c = effectiveAdminCreators.find(
      (x) => String(x?.email || "").trim() === String(creatorFilter).trim()
    );
    if (!c) return "#fff";
    if (c.role === "super.admin") return emailTextColorForRole("super.admin");
    if (c.role === "admin") return emailTextColorForRole("admin");
    if (c.role == null) return "#f1f5f9";
    return emailTextColorForRole("user");
  }, [creatorFilter, effectiveAdminCreators]);

  const globalBusy = loading || deleteBusy;

  const sendChatText = async () => {
    const text = String(chatDraft || "").trim();
    if (!selConv || !text) return;
    setChatErr("");
    setChatBusy(true);
    try {
      const u = auth?.currentUser;
      await sendMessage(selConv, {
        text,
        userId: u?.uid ?? null,
        username: u?.email || u?.displayName || "admin",
        role: "admin",
      });
      setChatDraft("");
    } catch (e) {
      setChatErr(e?.message || "Gửi thất bại");
    } finally {
      setChatBusy(false);
    }
  };

  useLayoutEffect(() => {
    const el = chatTextareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const sh = el.scrollHeight;
    const h = Math.min(SUPPORT_COMPOSER_TEXTAREA_MAX, Math.max(SUPPORT_COMPOSER_TEXTAREA_MIN, sh));
    el.style.height = `${h}px`;
    el.style.overflowY = sh > SUPPORT_COMPOSER_TEXTAREA_MAX ? "auto" : "hidden";
    if (!selConv) return;
    const end = supportMsgsEndRef.current;
    if (!end) return;
    try {
      end.scrollIntoView({ block: "end", inline: "nearest", behavior: "auto" });
    } catch {
      /* noop */
    }
  }, [chatDraft, selConv]);

  useLayoutEffect(() => {
    if (!selConv) return;
    const end = supportMsgsEndRef.current;
    if (!end) return;
    try {
      end.scrollIntoView({ block: "end", inline: "nearest", behavior: "auto" });
    } catch {
      /* noop */
    }
  }, [selConv, convMsgs]);

  const confirmSupportDeleteFn = async () => {
    const id = supportDeleteConfirm.id ? String(supportDeleteConfirm.id) : "";
    if (!id) return;
    setSupportDeleteBusy(true);
    setChatErr("");
    try {
      await deleteConversation(id);
      if (selConv === id) setSelConv("");
      setSupportReadAt((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setSupportDeleteConfirm({ open: false, id: "" });
    } catch (e) {
      setChatErr(e?.message || "Xoá hội thoại thất bại");
    } finally {
      setSupportDeleteBusy(false);
    }
  };

  const submitCreateUser = async () => {
    setCuOk("");
    setCuErr("");
    const email = String(cuEmail || "").trim();
    const password = String(cuPassword ?? "");
    if (!email) {
      setCuErr("Vui lòng nhập email.");
      return;
    }
    if (!isValidEmailLike(email)) {
      setCuErr("Email không hợp lệ.");
      return;
    }
    if (!password) {
      setCuErr("Vui lòng nhập mật khẩu.");
      return;
    }
    if (password.length < 6) {
      setCuErr("Mật khẩu nên có ít nhất 6 ký tự.");
      return;
    }
    const daysRaw = String(cuDays ?? "").replace(/[^\d]/g, "");
    const d = daysRaw === "" ? 0 : Number(daysRaw);
    if (!Number.isFinite(d) || d < 0) {
      setCuErr("Số ngày phải là số nguyên ≥ 0.");
      return;
    }
    const plan = canonicalPlanFromValue(cuPlan) || "basic";
    const roleToCreate = operatorRole === "super.admin" ? cuRole : "user";
    setCuBusy(true);
    try {
      const created = await createUser({ email, password, role: roleToCreate });
      const uid = created?.user?.uid;
      if (!uid) throw new Error("Không nhận được UID sau khi tạo tài khoản.");

      const expiresAt = Date.now() + Math.floor(d) * DAY_MS;
      await updateUser(uid, {
        plan,
        access: { mode: "days", expiresAt },
        analysisAccess: { blocked: false },
      });
      if (d > 0) await enableUser(uid);

      setCuEmail("");
      setCuPassword("");
      setCuRole("user");
      setCuPlan("basic");
      setCuDays("");
      setCuOk("Đã tạo tài khoản thành công.");
      await loadPage(page);
    } catch (err) {
      setCuErr(err?.message || "Tạo tài khoản thất bại");
    } finally {
      setCuBusy(false);
    }
  };

  return (
    <div style={{ padding: 10, maxWidth: "100%", overflowX: "hidden", paddingBottom: 78 }}>
      {/* Header: operator + role | Refresh + Logout */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, justifyContent: "space-between", minWidth: 0 }}>
        <div
          style={{
            minWidth: 0,
            flex: "1 1 auto",
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 10px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.15)",
            background: "rgba(0,0,0,0.25)",
            color: "#fff",
            fontWeight: 500,
            fontSize: 12,
            overflow: "hidden",
          }}
          title={
            adminEmail
              ? `${adminEmail} 👤 ${operatorRole === "super.admin" ? "super.admin" : operatorRole === "admin" ? "admin" : ""}`
              : "admin"
          }
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: emailTextColorForRole(operatorRole),
            }}
          >
            {adminEmail || "admin"}
          </span>
          <span style={{ opacity: 0.9, flexShrink: 0 }}>👤</span>
          {operatorRole === "super.admin" || operatorRole === "admin" ? (
            <span
              style={{
                fontWeight: 600,
                flexShrink: 0,
                color: emailTextColorForRole(operatorRole),
              }}
            >
              {operatorRole === "super.admin" ? "super.admin" : "admin"}
            </span>
          ) : null}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, flexWrap: "nowrap" }}>
          <button
            type="button"
            onClick={() => loadPage(page)}
            disabled={loading}
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "#fff",
              color: "#000",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "..." : "Refresh"}
          </button>

          <button
            type="button"
            onClick={onLogout}
            disabled={loading}
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              border: 0,
              background: "#ff3b30",
              color: "#fff",
              fontWeight: 650,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* One row: ~65% search | ~35% creator */}
      <div
        style={{
          marginBottom: 8,
          display: "flex",
          flexDirection: "row",
          flexWrap: "nowrap",
          gap: 8,
          alignItems: "stretch",
          width: "100%",
          minWidth: 0,
        }}
      >
        <div style={{ flex: "13 1 0", minWidth: 0 }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Tìm tài khoản…"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: 11,
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "rgba(0,0,0,0.35)",
              color: "#fff",
              outline: "none",
              fontWeight: 400,
            }}
          />
        </div>

        <div style={{ flex: "7 1 0", minWidth: 0 }}>
          <SliderSelect
            value={creatorFilter}
            onChange={setCreatorFilter}
            disabled={loading}
            darkOptions
            selectStyle={{
              color: creatorFilterSelectColor,
              background: "linear-gradient(180deg, rgba(15,23,42,0.72) 0%, rgba(0,0,0,0.38) 100%)",
              borderColor: "rgba(255,255,255,0.24)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
            }}
            options={[
              { value: "all", label: "All", optionStyle: { color: "#f8fafc" } },
              ...effectiveAdminCreators
                .filter((c) => isValidEmailLike(c?.email))
                .map((c) => {
                  const r =
                    c?.role === "super.admin" ? "super.admin" : c?.role === "admin" ? "admin" : "user";
                  const roleHue =
                    r === "super.admin" ? "#fde047" : r === "admin" ? "#bfdbfe" : "#f1f5f9";
                  return {
                    value: String(c.email || "").trim(),
                    label: String(c.email || "").trim(),
                    optionStyle: { color: roleHue },
                  };
                }),
            ]}
          />
        </div>
      </div>

      {error ? (
        <div
          style={{
            padding: 10,
            background: "#2a1111",
            border: "1px solid #b93a3a",
            borderRadius: 10,
            marginBottom: 10,
            color: "#fff",
            fontSize: 13,
            fontWeight: 400,
          }}
        >
          {error}
        </div>
      ) : null}

      {/* Table */}
      <div
        style={{
          overflowX: "hidden",
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.15)",
          background: "rgba(0,0,0,0.35)",
          width: "100%",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            color: "#fff",
            fontSize: 12,
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: "auto" }} />
            <col style={{ width: 64 }} />
            <col style={{ width: 188 }} />
          </colgroup>

          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: "left" }}>Tài Khoản</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Ngày</th>
              <th style={{ ...thStyle, textAlign: "right", paddingLeft: 4 }}>
                <div
                  style={{
                    display: "flex",
                    gap: 5,
                    justifyContent: "flex-end",
                    alignItems: "center",
                    width: "100%",
                    maxWidth: 188,
                    marginLeft: "auto",
                  }}
                >
                  <span style={{ flex: "1 1 0", minWidth: 0, textAlign: "center", fontWeight: 500, fontSize: 11 }}>
                    Status
                  </span>
                  <span style={{ flex: "1 1 0", minWidth: 0, textAlign: "center", fontWeight: 500, fontSize: 11 }}>
                    Plan
                  </span>
                </div>
              </th>
            </tr>
          </thead>

          <tbody>
            {displayUsers.flatMap((u) => {
              const busy = globalBusy || busyUid === u.uid;
              const planBusy = busyUid === u.uid;
              const canManage = canManageUserRow(operatorRole, myUid, u);
              const canDel = canDeleteUserRow(operatorRole, myUid, u);
              const rr = resolveRowRole(u);
              const email = u.email || "-";
              const emailOk = isValidEmailLike(email);
              const daysText = formatDaysHours(u.expiresAt, nowMs);
              const actionValue = u.disabled ? "lock" : "open";
              const isEditing = editUid === u.uid;
              const planValue = u.plan;

              const digits = String(editNum || "").replace(/[^\d]/g, "");
              const inputDisplay = `${editSign}${digits}`;

              const statusOptions = [
                { value: "open", label: "Active" },
                { value: "lock", label: "Stop" },
                ...(canDel ? [{ value: "delete", label: "Delete" }] : []),
              ];

              const togglePermissionPanel = () => {
                if (!isCurrentUserSuperAdmin || !canManage) return;
                setPermissionPanelUid((prev) => (prev === u.uid ? "" : u.uid));
              };

              const onEmailActivate = () => {
                if (!canManage) return;
                if (isCurrentUserSuperAdmin) togglePermissionPanel();
                else openEditor(u);
              };

              const permissionRow =
                isCurrentUserSuperAdmin && permissionPanelUid === u.uid ? (
                  <tr key={`${u.uid}__perm`}>
                    <td colSpan={3} style={{ ...tdStyle, paddingTop: 6, paddingBottom: 10 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                          maxWidth: "100%",
                        }}
                      >
                        <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.85)" }}>
                          Quyền tài khoản
                        </span>
                        <div style={{ flex: "1 1 auto", minWidth: 0, maxWidth: 200 }}>
                          <SliderSelect
                            variant="role"
                            value={rr}
                            disabled={busy || !canManage}
                            onChange={(v) => onRoleChange(u, v)}
                            options={[
                              { value: "user", label: "User" },
                              { value: "admin", label: "Admin" },
                              { value: "super.admin", label: "Super Admin" },
                            ]}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : null;

              const mainRow = (
                <tr key={u.uid}>
                  <td style={{ ...tdStyle, paddingRight: 6 }}>
                    <div
                      onClick={onEmailActivate}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onEmailActivate();
                        }
                      }}
                      style={{
                        fontWeight: 500,
                        color: emailTextColorForRole(rr),
                        cursor: canManage ? "pointer" : "not-allowed",
                        userSelect: "none",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                        opacity: busy ? 0.85 : 1,
                      }}
                      title={
                        !canManage
                          ? "Không có quyền chỉnh"
                          : isCurrentUserSuperAdmin
                            ? "Bấm để quản lý quyền (User / Admin / Super Admin)"
                            : "Bấm để chỉnh ngày sử dụng"
                      }
                    >
                      {emailOk ? email : "(Email không hợp lệ)"}
                    </div>
                  </td>

                  <td style={{ ...tdStyle, textAlign: "left", paddingLeft: 6, paddingRight: 4 }}>
                    <div
                      onClick={() => {
                        if (isCurrentUserSuperAdmin && canManage) openEditor(u);
                      }}
                      role={isCurrentUserSuperAdmin && canManage ? "button" : undefined}
                      tabIndex={isCurrentUserSuperAdmin && canManage ? 0 : undefined}
                      onKeyDown={(e) => {
                        if (!isCurrentUserSuperAdmin || !canManage) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openEditor(u);
                        }
                      }}
                      style={{
                        fontWeight: 500,
                        cursor: isCurrentUserSuperAdmin && canManage ? "pointer" : "default",
                        userSelect: isCurrentUserSuperAdmin && canManage ? "none" : undefined,
                      }}
                      title={
                        isCurrentUserSuperAdmin && canManage
                          ? "Bấm để chỉnh ngày sử dụng"
                          : undefined
                      }
                    >
                      {daysText}
                    </div>
                  </td>

                  <td style={{ ...tdStyle, textAlign: "right", paddingLeft: 2, paddingRight: 4 }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 5,
                        justifyContent: "flex-end",
                        alignItems: "stretch",
                        width: "100%",
                        maxWidth: 188,
                        marginLeft: "auto",
                        minWidth: 0,
                      }}
                    >
                      <div style={{ flex: "1 1 0", minWidth: 0, maxWidth: 92 }}>
                        <SliderSelect
                          variant="status"
                          value={actionValue}
                          disabled={busy || !canManage}
                          onChange={(v) => onActionChange(u, v)}
                          options={statusOptions}
                        />
                      </div>
                      <div style={{ flex: "1 1 0", minWidth: 0, maxWidth: 92 }}>
                        <SliderSelect
                          variant="plan"
                          value={planValue}
                          disabled={planBusy || !canManage}
                          onChange={(v) => onActionChange(u, v)}
                          options={[
                            { value: "basic", label: "Basic" },
                            { value: "pro", label: "Pro" },
                            { value: "premium", label: "Premium" },
                          ]}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              );

              if (!isEditing) return [mainRow, ...(permissionRow ? [permissionRow] : [])];

              const editorRow = (
                <tr key={`${u.uid}__editor`}>
                  <td colSpan={3} style={{ ...tdStyle, paddingTop: 6, paddingBottom: 10 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        flexWrap: "nowrap",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      <button
                        type="button"
                        disabled={busy || !canManage}
                        onClick={() => setEditSign("-")}
                        style={{
                          width: 34,
                          height: 34,
                          flex: "0 0 auto",
                          borderRadius: 10,
                          border: "1px solid rgba(255,255,255,0.18)",
                          background: "rgba(0,0,0,0.25)",
                          color: "#fff",
                          fontWeight: 600,
                          cursor: busy || !canManage ? "not-allowed" : "pointer",
                        }}
                      >
                        -
                      </button>

                      <input
                        type="text"
                        inputMode="numeric"
                        value={inputDisplay}
                        disabled={busy || !canManage}
                        onChange={(e) => {
                          const raw = String(e.target.value || "");
                          const onlyDigits = raw.replace(/[^\d]/g, "");
                          setEditNum(onlyDigits);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            saveDays(u);
                          }
                          if (e.key === "Escape") cancelEdit();
                        }}
                        style={{
                          width: 90,
                          flex: "0 0 auto",
                          padding: 9,
                          borderRadius: 10,
                          border: "1px solid rgba(255,255,255,0.15)",
                          background: "rgba(255,255,255,0.08)",
                          color: "#fff",
                          outline: "none",
                          fontWeight: 400,
                        }}
                      />

                      <button
                        type="button"
                        disabled={busy || !canManage}
                        onClick={() => setEditSign("+")}
                        style={{
                          width: 34,
                          height: 34,
                          flex: "0 0 auto",
                          borderRadius: 10,
                          border: "1px solid rgba(255,255,255,0.18)",
                          background: "rgba(0,0,0,0.25)",
                          color: "#fff",
                          fontWeight: 600,
                          cursor: busy || !canManage ? "not-allowed" : "pointer",
                        }}
                      >
                        +
                      </button>

                      <button
                        type="button"
                        disabled={busy || !canManage}
                        onClick={() => saveDays(u)}
                        style={{
                          padding: "9px 10px",
                          flex: "0 0 auto",
                          borderRadius: 10,
                          border: 0,
                          background: busy || !canManage ? "#4b7d1f" : "#7CFC00",
                          color: "#000",
                          fontWeight: 650,
                          cursor: busy || !canManage ? "not-allowed" : "pointer",
                        }}
                      >
                        Lưu
                      </button>

                      <button
                        type="button"
                        disabled={busy || !canManage}
                        onClick={cancelEdit}
                        style={{
                          padding: "9px 10px",
                          flex: "0 0 auto",
                          borderRadius: 10,
                          border: 0,
                          background: busy || !canManage ? "#7a2323" : "#ff3b30",
                          color: "#fff",
                          fontWeight: 650,
                          cursor: busy || !canManage ? "not-allowed" : "pointer",
                        }}
                      >
                        Huỷ
                      </button>
                    </div>
                  </td>
                </tr>
              );

              return [mainRow, ...(permissionRow ? [permissionRow] : []), editorRow];
            })}

            {!displayUsers.length ? (
              <tr>
                <td style={tdStyle} colSpan={3}>
                  Không có user phù hợp.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {openPanel === "create" ? (
        <div
          role="presentation"
          onClick={() => setOpenPanel(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9980,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 12,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-panel-create-title"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 400,
              maxWidth: "100%",
              maxHeight: "min(90vh, 640px)",
              overflow: "auto",
              background: "rgba(10,10,10,0.97)",
              border: "1px solid rgba(255,255,255,0.18)",
              borderRadius: 14,
              padding: 14,
              boxShadow: "0 18px 60px rgba(0,0,0,0.6)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div id="admin-panel-create-title" style={{ fontWeight: 700, fontSize: 15, color: "#fff" }}>
                Tạo tài khoản
              </div>
              <button
                type="button"
                aria-label="Đóng"
                onClick={() => setOpenPanel(null)}
                style={{
                  border: "none",
                  background: "rgba(255,255,255,0.1)",
                  color: "#fff",
                  borderRadius: 8,
                  width: 32,
                  height: 32,
                  cursor: "pointer",
                  fontSize: 20,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <input
                type="email"
                value={cuEmail}
                onChange={(e) => setCuEmail(e.target.value)}
                placeholder="Email"
                autoComplete="off"
                disabled={cuBusy}
                style={{
                  width: "100%",
                  padding: 9,
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(0,0,0,0.3)",
                  color: "#fff",
                  fontSize: 12,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              <input
                type="password"
                value={cuPassword}
                onChange={(e) => setCuPassword(e.target.value)}
                placeholder="Mật khẩu"
                autoComplete="new-password"
                disabled={cuBusy}
                style={{
                  width: "100%",
                  padding: 9,
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(0,0,0,0.3)",
                  color: "#fff",
                  fontSize: 12,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              <div style={{ flex: "1 1 100%", minWidth: 0 }}>
                <div style={{ fontSize: 10, opacity: 0.75, marginBottom: 4, color: "#fff" }}>Role</div>
                <SliderSelect
                  variant="role"
                  value={operatorRole === "super.admin" ? cuRole : "user"}
                  onChange={setCuRole}
                  disabled={cuBusy || operatorRole !== "super.admin"}
                  options={[
                    { value: "user", label: "User" },
                    { value: "admin", label: "Admin" },
                    { value: "super.admin", label: "Super Admin" },
                  ]}
                />
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ flex: "1 1 120px", minWidth: 0 }}>
                  <div style={{ fontSize: 10, opacity: 0.75, marginBottom: 4, color: "#fff" }}>Plan</div>
                  <SliderSelect
                    variant="plan"
                    value={cuPlan}
                    onChange={setCuPlan}
                    disabled={cuBusy}
                    options={[
                      { value: "basic", label: "Basic" },
                      { value: "pro", label: "Pro" },
                      { value: "premium", label: "Premium" },
                    ]}
                  />
                </div>
                <div style={{ flex: "1 1 100px", minWidth: 0 }}>
                  <div style={{ fontSize: 10, opacity: 0.75, marginBottom: 4, color: "#fff" }}>Số ngày</div>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={cuDays}
                    onChange={(e) => setCuDays(e.target.value.replace(/[^\d]/g, ""))}
                    placeholder="0"
                    disabled={cuBusy}
                    style={{
                      width: "100%",
                      padding: 9,
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.18)",
                      background: "rgba(0,0,0,0.3)",
                      color: "#fff",
                      fontSize: 12,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
              {cuErr ? (
                <div
                  style={{
                    padding: 8,
                    borderRadius: 8,
                    background: "rgba(127,29,29,0.35)",
                    border: "1px solid rgba(248,113,113,0.3)",
                    color: "#fecaca",
                    fontSize: 12,
                  }}
                >
                  {cuErr}
                </div>
              ) : null}
              {cuOk ? (
                <div
                  style={{
                    padding: 8,
                    borderRadius: 8,
                    background: "rgba(22,101,52,0.35)",
                    border: "1px solid rgba(74,222,128,0.25)",
                    color: "#bbf7d0",
                    fontSize: 12,
                  }}
                >
                  {cuOk}
                </div>
              ) : null}
              <button
                type="button"
                onClick={submitCreateUser}
                disabled={cuBusy}
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: 0,
                  background: cuBusy ? "#4b7d1f" : "#7CFC00",
                  color: "#000",
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: cuBusy ? "not-allowed" : "pointer",
                  marginTop: 4,
                }}
              >
                {cuBusy ? "Đang tạo…" : "Tạo tài khoản"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {openPanel === "support" ? (
        <div
          role="presentation"
          onClick={() => setOpenPanel(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9980,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "stretch",
            justifyContent: "stretch",
            padding: 0,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-panel-support-title"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100vw",
              height: "100vh",
              maxWidth: "100vw",
              maxHeight: "100vh",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              background: "linear-gradient(180deg, rgba(18,18,22,0.99) 0%, rgba(8,8,12,0.99) 100%)",
              border: "1px solid rgba(255,255,255,0.22)",
              borderRadius: 0,
              padding: 0,
              boxShadow: "0 24px 80px rgba(0,0,0,0.72), 0 0 0 1px rgba(255,255,255,0.04) inset",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: supportLayoutNarrow ? "10px 12px" : "12px 16px",
                flexShrink: 0,
                borderBottom: "1px solid rgba(255,255,255,0.14)",
                background: "rgba(0,0,0,0.35)",
              }}
            >
              <div id="admin-panel-support-title" style={{ fontWeight: 700, fontSize: 16, color: "#f8fafc", letterSpacing: 0.2 }}>
                Hỗ trợ trực tiếp
              </div>
              <button
                type="button"
                aria-label="Đóng"
                onClick={() => setOpenPanel(null)}
                style={{
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(255,255,255,0.08)",
                  color: "#e2e8f0",
                  borderRadius: 8,
                  width: 34,
                  height: 34,
                  cursor: "pointer",
                  fontSize: 20,
                  lineHeight: 1,
                  fontWeight: 500,
                }}
              >
                ×
              </button>
            </div>

            {supportLoadError ? (
              <div
                role="alert"
                style={{
                  flexShrink: 0,
                  padding: "8px 14px",
                  fontSize: 12,
                  color: "#fecaca",
                  background: "rgba(127,29,29,0.42)",
                  borderBottom: "1px solid rgba(248,113,113,0.28)",
                }}
              >
                {supportLoadError}
              </div>
            ) : null}

            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div
                style={{
                  flex: "2 2 0",
                  minHeight: supportLayoutNarrow ? 108 : 120,
                  maxHeight: supportLayoutNarrow ? 180 : 220,
                  display: "flex",
                  flexDirection: "column",
                  borderBottom: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(0,0,0,0.32)",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 1.2,
                    textTransform: "uppercase",
                    color: "rgba(226,232,240,0.55)",
                    padding: "8px 14px 4px",
                    flexShrink: 0,
                  }}
                >
                  Hàng chờ trượt ngang
                </div>
                <div
                  style={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflowX: "auto",
                    overflowY: "hidden",
                    WebkitOverflowScrolling: "touch",
                    scrollBehavior: "smooth",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "nowrap",
                      alignItems: "stretch",
                      gap: 8,
                      padding: "6px 12px 10px",
                      width: "max-content",
                      minWidth: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    {!supportQueue.length ? (
                      <div
                        style={{
                          fontSize: 12,
                          color: "rgba(226,232,240,0.62)",
                          lineHeight: 1.45,
                          padding: "6px 4px",
                          whiteSpace: "normal",
                          maxWidth: 520,
                        }}
                      >
                        Chưa có hội thoại nào. Cuộc trò chuyện từ người dùng sẽ xuất hiện ở đây.
                      </div>
                    ) : (
                      supportQueue.map((row) => {
                        const active = selConv === row.id;
                        const pb =
                          row.planLabel === "Premium"
                            ? { bg: "rgba(180,83,9,0.35)", bd: "rgba(251,191,36,0.55)", fg: "#fde68a", glow: "rgba(251,191,36,0.25)" }
                            : row.planLabel === "Pro"
                              ? { bg: "rgba(29,78,216,0.35)", bd: "rgba(96,165,250,0.5)", fg: "#bfdbfe", glow: "rgba(59,130,246,0.2)" }
                              : row.planLabel === "Basic"
                                ? { bg: "rgba(51,65,85,0.45)", bd: "rgba(148,163,184,0.45)", fg: "#e2e8f0", glow: "rgba(148,163,184,0.12)" }
                                : { bg: "rgba(88,28,135,0.38)", bd: "rgba(192,132,252,0.45)", fg: "#e9d5ff", glow: "rgba(168,85,247,0.2)" };
                        return (
                          <button
                            key={row.id}
                            type="button"
                            onClick={() => {
                              setSelConv(row.id);
                              setChatErr("");
                              markSupportConversationRead(row.id);
                            }}
                            style={{
                              flex: "0 0 auto",
                              maxWidth: 280,
                              textAlign: "left",
                              padding: "8px 12px 8px 10px",
                              borderRadius: 12,
                              border: active ? `2px solid ${pb.bd}` : `1px solid rgba(255,255,255,0.12)`,
                              background: active
                                ? `linear-gradient(180deg, ${pb.bg} 0%, rgba(0,0,0,0.45) 100%)`
                                : "rgba(15,23,42,0.65)",
                              boxShadow: active
                                ? `0 0 0 1px ${pb.glow}, 0 4px 18px rgba(0,0,0,0.45)`
                                : "0 2px 8px rgba(0,0,0,0.25)",
                              color: "#f1f5f9",
                              cursor: "pointer",
                              display: "flex",
                              flexDirection: "column",
                              gap: 6,
                              minWidth: 0,
                              whiteSpace: "nowrap",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 700,
                                  fontSize: 12,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  flex: 1,
                                  minWidth: 0,
                                  color: "#f8fafc",
                                }}
                              >
                                {row.title}
                              </div>
                              {row.waiting ? (
                                <span
                                  title="Chờ phản hồi"
                                  style={{
                                    flex: "0 0 auto",
                                    minWidth: 8,
                                    height: 8,
                                    borderRadius: 999,
                                    background: "#ef4444",
                                    boxShadow: "0 0 0 2px rgba(0,0,0,0.5), 0 0 10px rgba(239,68,68,0.65)",
                                  }}
                                />
                              ) : null}
                            </div>
                            <span
                              style={{
                                fontSize: 9,
                                fontWeight: 800,
                                letterSpacing: 0.4,
                                textTransform: "uppercase",
                                padding: "3px 7px",
                                borderRadius: 8,
                                border: `1px solid ${pb.bd}`,
                                background: pb.bg,
                                color: pb.fg,
                                alignSelf: "flex-start",
                              }}
                            >
                              {row.planLabel}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                <div
                  style={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: "8px 14px 10px",
                    background: "rgba(0,0,0,0.28)",
                  }}
                >
                  <button
                    type="button"
                    disabled={!selConv}
                    onClick={() => {
                      if (!selConv) return;
                      setSupportDeleteConfirm({ open: true, id: selConv });
                    }}
                    style={{
                      padding: "7px 12px",
                      borderRadius: 10,
                      border: selConv ? "1px solid rgba(248,113,113,0.5)" : "1px solid rgba(255,255,255,0.1)",
                      background: selConv ? "rgba(127,29,29,0.45)" : "rgba(255,255,255,0.04)",
                      color: selConv ? "#fecaca" : "rgba(248,250,252,0.35)",
                      fontWeight: 700,
                      fontSize: 11,
                      cursor: selConv ? "pointer" : "not-allowed",
                      flexShrink: 0,
                    }}
                  >
                    Xoá cuộc trò chuyện
                  </button>
                  <div style={{ minWidth: 0, flex: 1, textAlign: "right" }}>
                    {!selConv ? (
                      <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(226,232,240,0.5)" }}>Chưa chọn hội thoại</div>
                    ) : (
                      <>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: "#f8fafc", wordBreak: "break-word" }}>
                            {selectedSupportRow?.title || "Vô Danh"}
                          </span>
                          {selectedSupportRow ? (
                            <span
                              style={{
                                fontSize: 9,
                                fontWeight: 800,
                                letterSpacing: 0.35,
                                textTransform: "uppercase",
                                padding: "3px 8px",
                                borderRadius: 8,
                                border:
                                  selectedSupportRow.planLabel === "Premium"
                                    ? "1px solid rgba(251,191,36,0.55)"
                                    : selectedSupportRow.planLabel === "Pro"
                                      ? "1px solid rgba(96,165,250,0.5)"
                                      : selectedSupportRow.planLabel === "Basic"
                                        ? "1px solid rgba(148,163,184,0.45)"
                                        : "1px solid rgba(192,132,252,0.45)",
                                background:
                                  selectedSupportRow.planLabel === "Premium"
                                    ? "rgba(180,83,9,0.35)"
                                    : selectedSupportRow.planLabel === "Pro"
                                      ? "rgba(29,78,216,0.35)"
                                      : selectedSupportRow.planLabel === "Basic"
                                        ? "rgba(51,65,85,0.45)"
                                        : "rgba(88,28,135,0.38)",
                                color:
                                  selectedSupportRow.planLabel === "Premium"
                                    ? "#fde68a"
                                    : selectedSupportRow.planLabel === "Pro"
                                      ? "#bfdbfe"
                                      : selectedSupportRow.planLabel === "Basic"
                                        ? "#e2e8f0"
                                        : "#e9d5ff",
                              }}
                            >
                              {selectedSupportRow.planLabel}
                            </span>
                          ) : null}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "rgba(226,232,240,0.42)",
                            marginTop: 4,
                            fontFamily: "ui-monospace, monospace",
                            wordBreak: "break-all",
                          }}
                        >
                          {selConv ? `ID: ${selConv}` : ""}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div
                style={{
                  flex: "7 7 0",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  background: "rgba(0,0,0,0.22)",
                }}
              >
                {!selConv ? (
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "rgba(226,232,240,0.55)",
                      fontSize: 14,
                      padding: 24,
                      textAlign: "center",
                      gap: 10,
                      minHeight: 0,
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "rgba(248,250,252,0.75)", fontSize: 15 }}>Chưa chọn hội thoại</div>
                    <div style={{ fontSize: 13, maxWidth: 360, lineHeight: 1.5, color: "rgba(226,232,240,0.58)" }}>
                      Chọn một tab trong hàng chờ phía trên để xem nội dung và trả lời.
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      overflowX: "hidden",
                      padding: "14px 16px 6px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                      WebkitOverflowScrolling: "touch",
                    }}
                  >
                    {!(convMsgs && convMsgs.length) ? (
                      <div style={{ fontSize: 13, color: "rgba(226,232,240,0.55)", paddingTop: 6 }}>Chưa có tin nhắn.</div>
                    ) : (
                      convMsgs.map((m, mi) => {
                        const mid = m?.id != null ? String(m.id) : `m-${mi}`;
                        const role = String(m?.role || "user").toLowerCase();
                        const typ = String(m?.type || "text").toLowerCase();
                        const isSystem = role === "system" || typ === "system";
                        const isAdmin = role === "admin";
                        const body = m?.text != null ? String(m.text) : "";
                        const tlabel = formatMsgTime(m?.createdAt);
                        const bubbleMax = "min(480px, 88%)";
                        if (isSystem) {
                          return (
                            <div
                              key={mid}
                              style={{
                                alignSelf: "center",
                                maxWidth: "92%",
                                padding: "10px 16px",
                                borderRadius: 12,
                                background: "rgba(30,41,59,0.75)",
                                border: "1px solid rgba(148,163,184,0.2)",
                                color: "rgba(226,232,240,0.88)",
                                fontSize: 12,
                                textAlign: "center",
                                lineHeight: 1.5,
                              }}
                            >
                              {body || "—"}
                              {tlabel ? (
                                <div style={{ fontSize: 10, color: "rgba(148,163,184,0.85)", marginTop: 8 }}>{tlabel}</div>
                              ) : null}
                            </div>
                          );
                        }
                        if (typ === "image" && body) {
                          return (
                            <div
                              key={mid}
                              style={{
                                alignSelf: isAdmin ? "flex-end" : "flex-start",
                                maxWidth: bubbleMax,
                              }}
                            >
                              <img
                                src={body}
                                alt=""
                                style={{
                                  maxWidth: "100%",
                                  width: "auto",
                                  maxHeight: 300,
                                  borderRadius: 14,
                                  display: "block",
                                  border: "1px solid rgba(255,255,255,0.16)",
                                  boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
                                }}
                              />
                              {tlabel ? (
                                <div
                                  style={{
                                    fontSize: 10,
                                    marginTop: 8,
                                    textAlign: isAdmin ? "right" : "left",
                                    color: "rgba(148,163,184,0.85)",
                                  }}
                                >
                                  {tlabel}
                                </div>
                              ) : null}
                            </div>
                          );
                        }
                        return (
                          <div
                            key={mid}
                            style={{
                              alignSelf: isAdmin ? "flex-end" : "flex-start",
                              maxWidth: bubbleMax,
                              padding: "11px 15px",
                              borderRadius: 16,
                              background: isAdmin
                                ? "linear-gradient(165deg, rgba(37,99,235,0.5) 0%, rgba(15,23,42,0.85) 100%)"
                                : "rgba(30,41,59,0.92)",
                              border: isAdmin ? "1px solid rgba(96,165,250,0.38)" : "1px solid rgba(148,163,184,0.22)",
                              color: "#f1f5f9",
                              fontSize: 13,
                              lineHeight: 1.5,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              boxShadow: isAdmin ? "0 4px 16px rgba(37,99,235,0.15)" : "0 2px 12px rgba(0,0,0,0.2)",
                            }}
                          >
                            {body || "—"}
                            {tlabel ? (
                              <div style={{ fontSize: 10, marginTop: 8, color: "rgba(191,219,254,0.75)" }}>{tlabel}</div>
                            ) : null}
                          </div>
                        );
                      })
                    )}
                    <div
                      ref={supportMsgsEndRef}
                      style={{
                        height: 1,
                        width: "100%",
                        flexShrink: 0,
                        overflow: "hidden",
                        pointerEvents: "none",
                      }}
                      aria-hidden
                    />
                  </div>
                )}
              </div>

              {chatErr ? (
                <div
                  style={{
                    flexShrink: 0,
                    padding: "8px 14px",
                    fontSize: 12,
                    color: "#fecaca",
                    background: "rgba(127,29,29,0.42)",
                    borderBottom: "1px solid rgba(248,113,113,0.28)",
                  }}
                >
                  {chatErr}
                </div>
              ) : null}

              <div
                style={{
                  flex: "0 0 auto",
                  display: "flex",
                  gap: 8,
                  padding: "6px 12px 8px",
                  alignItems: "flex-end",
                  flexShrink: 0,
                  background: "rgba(0,0,0,0.42)",
                  borderTop: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <textarea
                  ref={chatTextareaRef}
                  value={chatDraft}
                  onChange={(e) => setChatDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (selConv) sendChatText();
                    }
                  }}
                  placeholder={
                    selConv ? "Nhập nội dung trả lời…" : "Chọn một cuộc trò chuyện để trả lời…"
                  }
                  disabled={chatBusy || !selConv}
                  rows={1}
                  style={{
                    flex: "1 1 auto",
                    minWidth: 0,
                    minHeight: SUPPORT_COMPOSER_TEXTAREA_MIN,
                    maxHeight: SUPPORT_COMPOSER_TEXTAREA_MAX,
                    resize: "none",
                    padding: "7px 10px",
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.22)",
                    background: selConv ? "rgba(15,23,42,0.92)" : "rgba(15,23,42,0.45)",
                    color: selConv ? "#f8fafc" : "rgba(248,250,252,0.45)",
                    fontSize: 13,
                    outline: "none",
                    lineHeight: 1.4,
                    boxSizing: "border-box",
                    overflow: "hidden",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
                  }}
                />
                <button
                  type="button"
                  disabled={chatBusy || !selConv || !String(chatDraft || "").trim()}
                  onClick={sendChatText}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 9,
                    border: 0,
                    background:
                      chatBusy || !selConv || !String(chatDraft || "").trim() ? "rgba(74,222,128,0.25)" : "#7CFC00",
                    color: "#0f172a",
                    fontWeight: 800,
                    fontSize: 12,
                    lineHeight: 1.2,
                    cursor:
                      chatBusy || !selConv || !String(chatDraft || "").trim() ? "not-allowed" : "pointer",
                    flex: "0 0 auto",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 56,
                    alignSelf: "flex-end",
                  }}
                >
                  Gửi
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        open={confirm.open}
        title="Xác nhận xoá"
        desc={`Bạn có chắc muốn xoá tài khoản này không?\n\n${confirm.email}`}
        busy={deleteBusy}
        onCancel={() => setConfirm(EMPTY_CONFIRM)}
        onConfirm={confirmDeleteFn}
      />

      <ConfirmModal
        open={supportDeleteConfirm.open}
        title="Xoá toàn bộ hội thoại?"
        desc={`Hành động này sẽ xoá vĩnh viễn toàn bộ cuộc trò chuyện và mọi tin nhắn trong đó (không thể hoàn tác).\n\nHội thoại: ${supportDeleteConfirm.id || ""}`}
        busy={supportDeleteBusy}
        onCancel={() => setSupportDeleteConfirm({ open: false, id: "" })}
        onConfirm={confirmSupportDeleteFn}
      />

      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 10,
          zIndex: 50,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            pointerEvents: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 12px",
            borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.18)",
            background: "rgba(0,0,0,0.75)",
            backdropFilter: "blur(8px)",
            color: "#fff",
            flexWrap: "wrap",
            justifyContent: "center",
            maxWidth: "calc(100vw - 24px)",
          }}
        >
          <button
            type="button"
            aria-label="Tạo tài khoản"
            title="Tạo tài khoản"
            onClick={toggleCreatePanel}
            style={{
              width: 36,
              height: 36,
              padding: 0,
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.22)",
              background: openPanel === "create" ? "rgba(59,130,246,0.28)" : "rgba(0,0,0,0.35)",
              color: "#fff",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="8.5" cy="7" r="4" />
              <line x1="20" y1="8" x2="20" y2="14" />
              <line x1="17" y1="11" x2="23" y2="11" />
            </svg>
          </button>

          <button
            type="button"
            disabled={!canPrev}
            onClick={goPrev}
            style={{
              padding: "8px 12px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "rgba(0,0,0,0.25)",
              color: "#fff",
              fontWeight: 600,
              cursor: !canPrev ? "not-allowed" : "pointer",
              opacity: !canPrev ? 0.7 : 1,
            }}
          >
            Prev
          </button>

          <div style={{ fontSize: 12, fontWeight: 700, minWidth: 64, textAlign: "center" }}>{pageLabel}</div>

          <button
            type="button"
            disabled={!canNext}
            onClick={goNext}
            style={{
              padding: "8px 12px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "rgba(0,0,0,0.25)",
              color: "#fff",
              fontWeight: 600,
              cursor: !canNext ? "not-allowed" : "pointer",
              opacity: !canNext ? 0.7 : 1,
            }}
          >
            Next
          </button>

          <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
            <button
              type="button"
              aria-label="Hỗ trợ trực tiếp"
              title="Hỗ trợ trực tiếp"
              onClick={toggleSupportPanel}
              style={{
                width: 36,
                height: 36,
                padding: 0,
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.22)",
                background: openPanel === "support" ? "rgba(59,130,246,0.28)" : "rgba(0,0,0,0.35)",
                color: "#fff",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </button>
            {supportWaitingCount > 0 ? (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  top: -3,
                  right: -3,
                  minWidth: 17,
                  height: 17,
                  padding: "0 5px",
                  borderRadius: 999,
                  background: "#dc2626",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 800,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid rgba(0,0,0,0.82)",
                  boxSizing: "border-box",
                  lineHeight: 1,
                  pointerEvents: "none",
                }}
              >
                {supportWaitingCount > 99 ? "99+" : supportWaitingCount}
              </span>
            ) : null}
          </span>
        </div>
      </div>
    </div>
  );
}

// ===== styles =====
const thStyle = {
  textAlign: "left",
  padding: "9px 8px",
  fontWeight: 500,
  fontSize: 12,
  borderBottom: "1px solid rgba(255,255,255,0.15)",
  background: "rgba(0,0,0,0.35)",
  position: "sticky",
  top: 0,
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "9px 8px",
  borderBottom: "1px solid rgba(255,255,255,0.1)",
  verticalAlign: "top",
  overflow: "hidden",
};
